import { getCountryCode } from "countries-list";
import { Button } from "primereact/button";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { Rating } from "primereact/rating";
import { TabPanel, TabView } from "primereact/tabview";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import Flag from "react-world-flags";
import { PRODUCT_TYPE_CODES } from "../../constants/product-types";
import { modifyImageUrl } from "../../helpers/formatImageURL";
import { getCurrentLanguage } from "../../helpers/languageHelper";
import useCurrency from "../../hook/useCurrency";
import LoadingButton from "../common/LoadingButton";

const ShoppingCart = forwardRef(({ props }, ref) => {
  const {
    cartDetail,
    calculatePrice,
    getRealTimeLecturePrice,
    translations,
    handleDelete,
    goToNextStep,
    toggleProductSelection,
    selectAllProducts,
    getProductTypeClass,
    getProductTypeTag,
    isProductType,
    handleBundleChange,
    getProductNote,
  } = props;

  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showConfirmDeleteCartItemDialog, setShowConfirmDeleteCartItemDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { display, defaultCurrency, target } = useCurrency();
  const language = getCurrentLanguage();

  const handleCloseConfirmDeleteCartItemDialog = () => {
    setShowConfirmDeleteCartItemDialog(false);
  };

  const handleDeleteItem = async (item) => {
    setDeleteLoading(true);
    try {
      await handleDelete(item);
    } finally {
      setDeleteLoading(false);
      setShowConfirmDeleteCartItemDialog(false);
    }
  };

  const isCartValid = () => {
    if (!cartDetail || !Array.isArray(cartDetail.shoppingCartItems)) return false;

    const selectedItems = cartDetail.shoppingCartItems.filter((item) => item.isSelected);

    if (selectedItems.length === 0) return false;

    const allValid = selectedItems.every((item) => {
      if (isProductType(item.productTypeId, PRODUCT_TYPE_CODES.REAL_TIME_LECTURE)) {
        return item.selectedBundleId != null;
      }
      return true;
    });

    return allValid;
  };

  const getSplitMessageParts = (message) => {
    if (message.includes("{{name}}")) return message.split("{{name}}");
    if (message.includes("{{title}}")) return message.split("{{title}}");
    return [message, ""];
  };

  const confirmDeleteCartItem = (item) => {
    const messageTemplate = isProductType(item?.productTypeId, PRODUCT_TYPE_CODES.REAL_TIME_LECTURE) ? translations.removeTicketLabel : translations.removeItemLabel;
    const [before, after] = getSplitMessageParts(messageTemplate);
    confirmDialog({
      header: translations.removeCartItemHeader,
      message: (
        <span>
          {before}
          <strong>{item?.name}</strong>
          {after}
        </span>
      ),
      footer: (
        <div className="flex justify-content-end">
          <Button className="keduall-buttons button-text-normal-secondary" onClick={() => setShowConfirmDeleteCartItemDialog(false)}>
            <p className="keduall-typography text-16-semibold-600">{translations.cancelButtonLabel}</p>
          </Button>
          <LoadingButton className="keduall-buttons button-filled-default-normal-primary" loading={deleteLoading} onClick={() => handleDeleteItem(item)}>
            <p className="keduall-typography text-16-semibold-600 white-color">{translations.confirmButtonLabel}</p>
          </LoadingButton>
        </div>
      ),
    });
    setShowConfirmDeleteCartItemDialog(true);
  };

  useEffect(() => {
    if (!cartDetail || !Array.isArray(cartDetail.shoppingCartItems)) {
      setIsSelectAll(false);
      return;
    }
    const allSelected = cartDetail.shoppingCartItems.length > 0 && cartDetail.shoppingCartItems.every((item) => item.isSelected);
    setIsSelectAll(allSelected);
  }, [cartDetail]);

  useImperativeHandle(ref, () => ({
    handleCloseConfirmDeleteCartItemDialog,
  }));

  const tabPanels = [
    {
      header: `${translations.productLabel} (${cartDetail.shoppingCartItems.length} ${translations.itemsLabel})`,
      content: (
        <>
          <div className="flex flex-column p-3 gap-4 shopping-cart-list keduall-scrollbar">
            <div className="flex align-items-center gap-2">
              <input
                type="checkbox"
                id="selectAll"
                name="selectAll"
                value="selectAll"
                checked={isSelectAll}
                onChange={(e) => {
                  setIsSelectAll(e.target.checked);
                  selectAllProducts();
                }}
              />
              <label htmlFor="selectAll" className="keduall-checkbox checkbox-base checkbox-small">
                <i className="pi pi-check"></i>
              </label>
              <label className="keduall-typography text-16-normal-400">{translations.selectAllLabel}</label>
            </div>
            <div className="flex flex-column gap-5">
              {cartDetail.shoppingCartItems.length > 0 ? (
                cartDetail.shoppingCartItems.map(
                  (item) =>
                    !item.isQuickBuyItem && (
                      <div key={item.id}>
                        <div className="w-full grid">
                          {/* Checkbox Column */}
                          <div className="col-fixed md:flex align-items-center justify-content-center hidden">
                            <input id={`item-${item.id}`} type="checkbox" checked={item.isSelected} onChange={() => toggleProductSelection(item.id)} />
                            <label htmlFor={`item-${item.id}`} className="keduall-checkbox checkbox-base checkbox-small">
                              <i className="pi pi-check"></i>
                            </label>
                          </div>

                          {/* Course Details Column */}
                          <div className="col grid grid-nogutter keduall-card product-item-container gap-4 md:gap-0 p-3 ml-2">
                            <div className="flex w-full md:hidden justify-content-between">
                              <div className="col-fixed flex align-items-center justify-content-center w-40">
                                <input id={`item-${item.id}`} type="checkbox" checked={item.isSelected} onChange={() => toggleProductSelection(item.id)} />
                                <label htmlFor={`item-${item.id}`} className="keduall-checkbox checkbox-base checkbox-small">
                                  <i className="pi pi-check"></i>
                                </label>
                              </div>
                              <Button icon="pi pi-trash" className="p-button-text keduall-typography text-default-txt" onClick={() => confirmDeleteCartItem(item)} />
                            </div>
                            <div
                              className={`col-12 md:col-2 border-round-lg overflow-hidden relative p-0 h-max ${
                                isProductType(item.productTypeId, PRODUCT_TYPE_CODES.REAL_TIME_LECTURE) ? "aspect-ratio-1-1" : "aspect-ratio-16-9"
                              }`}
                            >
                              {item.thumbnailUrl ? (
                                <img src={modifyImageUrl(item.thumbnailUrl)} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex align-items-center justify-content-center surface-200">
                                  <i className="pi pi-ticket keduall-typography text-primary-default"></i>
                                </div>
                              )}
                              <span
                                className={`p-2 course-tag absolute keduall-typography text-white-txt text-center white-space-nowrap top-0 left-0 keduall-typography text-14-bold-700 ${getProductTypeClass(
                                  item.productTypeId,
                                )}`}
                              >
                                {getProductTypeTag(item.productTypeId)}
                              </span>
                            </div>

                            <div className="col-12 md:col-10 grid grid-nogutter">
                              <div className="col-12 md:col-8 md:px-4 mb-4 md:mb-0">
                                <div className="product-name flex align-items-center gap-2 py-2">
                                  {isProductType(item.productTypeId, PRODUCT_TYPE_CODES.REAL_TIME_LECTURE) ? (
                                    <a
                                      href={`/${language}/real-time-lectures/${item.entityPublicId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="keduall-typography text-16-semibold-600 hover:underline"
                                    >
                                      {item.name}
                                    </a>
                                  ) : (
                                    <span className="keduall-typography text-16-semibold-600">{item.name}</span>
                                  )}

                                  {isProductType(item.productTypeId, PRODUCT_TYPE_CODES.REAL_TIME_LECTURE) && (
                                    <Flag code={getCountryCode(item.trainerCountry)} className="tutor-flag" />
                                  )}
                                </div>
                                {!isProductType(item.productTypeId, PRODUCT_TYPE_CODES.EVENT) && !isProductType(item.productTypeId, PRODUCT_TYPE_CODES.PACKAGE) && !isProductType(item.productTypeId, PRODUCT_TYPE_CODES.DOTORE_PASS) && (
                                  <div className="mt-1 flex gap-2">
                                    <Rating className="keduall-rate rate-medium" value={item.rating} readOnly cancel={false} />
                                    <div className="keduall-typography text-14-normal-400">
                                      {item.rating} {translations.starsLabel}
                                    </div>
                                  </div>
                                )}
                                <div className="mt-1 keduall-typography text-14-normal-400 keduall-text two-row-ellipsis text-secondary-txt">{item.description}</div>
                              </div>
                              <div className="col-12 md:col-4 flex md:flex-column justify-content-right align-items-end gap-3">
                                <div className="flex justify-content-right  w-full">
                                  <div className="flex flex-column gap-1 justify-content-right  w-full m-auto">
                                    <div>
                                      {item.discountPercent > 0 && (
                                        <div className="keduall-typography heading-h6-medium-500 keduall-price keduall-original-price text-right w-full overflow-hidden text-overflow-ellipsis">
                                          {isProductType(item.productTypeId, PRODUCT_TYPE_CODES.REAL_TIME_LECTURE)
                                            ? display(getRealTimeLecturePrice(item).originalPrice, item.currencyCode ?? defaultCurrency)
                                            : display(item.price, item.currencyCode ?? defaultCurrency)}
                                        </div>
                                      )}
                                      <div className="keduall-typography heading-h4-semibold-600 keduall-price keduall-final-price text-right w-full overflow-hidden text-overflow-ellipsis">
                                        {isProductType(item.productTypeId, PRODUCT_TYPE_CODES.REAL_TIME_LECTURE)
                                          ? display(getRealTimeLecturePrice(item).discountPrice, item.currencyCode ?? defaultCurrency)
                                          : display(item.discountPrice, item.currencyCode ?? defaultCurrency)}
                                      </div>
                                    </div>
                                    <span className="keduall-typography text-14-normal-400 text-secondary-txt white-space-nowrap text-right w-full">{getProductNote(item)}</span>
                                  </div>
                                </div>
                                {isProductType(item.productTypeId, PRODUCT_TYPE_CODES.REAL_TIME_LECTURE) && (
                                  <Dropdown
                                    id={`dropdown-${item.id}`}
                                    name={`dropdown-${item.id}`}
                                    value={item.selectedBundleId}
                                    options={item.bundleDiscountOptions}
                                    onChange={(e) => handleBundleChange(item.id, e.value)}
                                    placeholder={translations.chooseBundleDiscount}
                                  />
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="pc-only col-fixed hidden md:flex align-items-center justify-content-center">
                            <Button icon="pi pi-trash" className="p-button-text keduall-typography text-default-txt" onClick={() => confirmDeleteCartItem(item)} />
                          </div>
                        </div>
                      </div>
                    ),
                )
              ) : (
                <div className="text-center text-gray-500 text-14 keduall-typography-normal-400">{translations.emptyCart}</div>
              )}
            </div>
          </div>
        </>
      ),
    },
  ];

  useEffect(() => {
    const footer = document.getElementById("footer");
    if (footer) {
      footer.classList.add("shopping-cart-step-1-footer");
    }

    return () => {
      if (footer) {
        footer.classList.remove("shopping-cart-step-1-footer");
      }
    };
  }, []);

  return (
    <div className="keduall-tab-panel">
      <TabView>
        {tabPanels.map((tab, index) => (
          <TabPanel className="keduall-typography text-16-semibold-600" key={index} header={tab.header}>
            {tab.content}
          </TabPanel>
        ))}
      </TabView>
      {/* Fixed Bottom Order Summary */}
      <div className="sticky bottom-0 w-full bg-white bottom-order-summary">
        <div className="flex flex-column gap-2">
          <div className="keduall-typography heading-h4-semibold-600">{translations.orderSummary}</div>
          <div className="grid">
            <div className="col-12 sm:col-6 grid align-items-center">
              <div className="col-12 lg:col-6 keduall-typography heading-h6-normal-400">
                {translations.allOrders} ({cartDetail.shoppingCartItems.filter((item) => item.isSelected).length} {translations.itemsLabel}):
              </div>
              <div className="pc-only col-12 lg:col-6 keduall-typography heading-h5-bold-700 text-right">{display(calculatePrice().subTotal, target)}</div>
              <div className="mobile-only  col-12 lg:col-6 keduall-typography heading-h5-bold-700 text-left">{display(calculatePrice().subTotal, target)}</div>
            </div>
            <Divider className="pc-only" layout="vertical" />
            <div className="col-12 sm:col-6 grid align-items-center">
              <div className="col-12 lg:col-6 keduall-typography heading-h3-semibold-600">{translations.total}:</div>
              <div className="pc-only col-12 lg:col-6 keduall-typography heading-h3-semibold-600 text-primary-default text-right">{display(calculatePrice().subTotal, target)}</div>
              <div className="mobile-only col-12 lg:col-6 keduall-typography heading-h3-semibold-600 text-primary-default text-left">
                {display(calculatePrice().subTotal, target)}
              </div>
            </div>
          </div>
          <Divider className="pc-only" />
          {/* Pay Now Button */}
          <div className="flex justify-content-end">
            <Button
              label={translations.payNowButton}
              className="pc-only keduall-buttons button-filled-default-normal-primary keduall-typography text-16-semibold-600"
              disabled={!isCartValid()}
              onClick={() => {
                goToNextStep();
              }}
            />
            <Button
              label={translations.payNowButton}
              className="w-full mobile-only keduall-buttons button-filled-default-normal-primary keduall-typography text-16-semibold-600"
              disabled={!isCartValid()}
              onClick={() => {
                goToNextStep();
              }}
            />
          </div>
        </div>
      </div>
      <ConfirmDialog className="keduall-confirm-dialog" draggable={false} visible={showConfirmDeleteCartItemDialog} onHide={() => setShowConfirmDeleteCartItemDialog(false)} />
    </div>
  );
});

export default ShoppingCart;
